import React, { useState, useEffect } from 'react';
import { useAlert } from '../../../providers/AlertProvider';
import { useAuthProvider } from '../../../providers/AuthProvider';
import { validatePassword } from "../../../api/profiles";
import { downloadCSV } from '../../lib/functions';
import Button from '../Button';
import logger from "../../../utils/logger";

import RequestPassword from "../RequestPassword";
import "../../css/GlobalStyles.css";

const SubcontractedRoutesReport = ({ subcontractedData = [], theme }) => {
    const { profile } = useAuthProvider();
    const { showAlert } = useAlert();

    const [tableData, setTableData] = useState([]);
    const [isFiltered, setIsFiltered] = useState(false);
    const [summaryStats, setSummaryStats] = useState({});
    const [mtwFilter, setMtwFilter] = useState(false);

    const isLightTheme = theme === 'light';

    const displayColumns = [
        { key: 'date', label: 'Date' },
        { key: 'route_ref', label: 'Route Ref' },
        { key: 'job', label: 'Customer' },
        { key: 'route', label: 'Route' },
        { key: 'subbed_company', label: 'Contractor' },
        { key: 'subbed_price', label: 'Subbed Price' },
        { key: 'subbed_ref', label: 'Subbed Ref' },
        { key: 'isMTW', label: 'MTW' },
        { key: 'job_comments', label: 'Comments' },
        { key: 'demurrage', label: 'Demurrage' },
        { key: 'demurrage_reason', label: 'Demurrage Reason' },
        { key: 'demurrage_cost', label: 'Demurrage Cost' },
        { key: 'costings', label: 'Our Price' },
    ];

    // Request Password States =======================================================================================================
    const [openRequestPassword, setOpenRequestPassword] = useState(false);
    const [allowDownload, setAllowDownload] = useState(false);

    useEffect(() => {
        setOpenRequestPassword(true); // Always open password modal when component mounts
    }, []);
    
    const handlePasswordSubmission = async (password) => {
        const email = profile.email;
    
        try {
            const { status } = await validatePassword(password, email);
            if (status === 200) {
                showData();
                setAllowDownload(true); // Allow download if password is valid
            } else {
                logger.log("Invalid password.");
                subcontractedData.length = 0; // Clear subcontractedData if password is invalid
                showAlert({
                    message: 'Password verification failed.',
                    type: 'danger',
                    buttons: [{ type: 'danger', label: 'Close' }],
                });
            }
        } catch (error) {
            logger.error("Error verifying password:", error);
            showAlert({
                message: 'An error occurred during password verification.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        }
        setOpenRequestPassword(false); // Close the modal
    };
    
    const showData = () => {
        if (subcontractedData && subcontractedData.length > 0) {
            // Format the data for display
            const formatted = subcontractedData.map((entry) => {
                const newEntry = {};
                displayColumns.forEach(({ key }) => {
                    if (key === 'date') {
                        const dateObj = new Date(entry[key]);
                        newEntry[key] = isNaN(dateObj)
                            ? entry[key]
                            : `${String(dateObj.getDate()).padStart(2, '0')}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${dateObj.getFullYear()}`;
                    } else if (key === 'isMTW') {
                        newEntry[key] = entry[key] ? 'Yes' : 'No';
                    } else if (key === 'subbed_price' || key === 'demurrage_cost' || key === 'costings') {
                        // Format currency fields
                        const value = parseFloat(entry[key]);
                        newEntry[key] = !isNaN(value) ? `£${value.toFixed(2)}` : '£0.00';
                    } else {
                        newEntry[key] = entry[key] ?? '—';
                    }
                });
                return newEntry;
            });
            setTableData(formatted);
            calculateSummaryStats(formatted);
        }
    };

    const calculateSummaryStats = (data) => {
        if (!data || data.length === 0) {
            setSummaryStats({});
            return;
        }

        // Parse currency values for calculations
        const parsePrice = (priceValue) => {
            if (!priceValue || priceValue === '—') return 0;
            // If it's already a number, return it
            if (typeof priceValue === 'number') return priceValue;
            // If it's a string, clean it up and parse
            if (typeof priceValue === 'string') {
                return parseFloat(priceValue.replace('£', '').replace(',', '')) || 0;
            }
            return 0;
        };

        const stats = {
            totalRoutes: data.length,
            totalSubbedPrice: data.reduce((sum, item) => sum + parsePrice(item.subbed_price), 0),
            totalOurPrice: data.reduce((sum, item) => sum + parsePrice(item.costings), 0),
            totalDemurrageCost: data.reduce((sum, item) => sum + parsePrice(item.demurrage_cost), 0),
            uniqueSubbedCompanies: [...new Set(data.map(item => item.subbed_company).filter(company => company && company !== '—'))].length,
            mtwRoutes: data.filter(item => item.isMTW === true || item.isMTW === 'Yes').length,
            demurrageRoutes: data.filter(item => item.demurrage && item.demurrage !== '—' && item.demurrage !== 'No').length,
        };

        // Calculate profit/loss
        stats.profitLoss = stats.totalOurPrice - stats.totalSubbedPrice - stats.totalDemurrageCost;
        
        // Calculate profit percentage
        stats.profitPercentage = stats.totalOurPrice > 0 
            ? ((stats.profitLoss / stats.totalOurPrice) * 100)
            : 0;

        setSummaryStats(stats);
    };



    const formatCurrency = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '£0.00';
        return `£${parseFloat(value).toFixed(2)}`;
    };



    const filterByCompany = (company) => {
        if (!company || company === 'all') {
            showData();
            setIsFiltered(false);
        } else {
            const filtered = subcontractedData.filter(entry => 
                entry.subbed_company?.toLowerCase().includes(company.toLowerCase())
            );
            const formatted = filtered.map((entry) => {
                const newEntry = {};
                displayColumns.forEach(({ key }) => {
                    if (key === 'date') {
                        const dateObj = new Date(entry[key]);
                        newEntry[key] = isNaN(dateObj)
                            ? entry[key]
                            : `${String(dateObj.getDate()).padStart(2, '0')}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${dateObj.getFullYear()}`;
                    } else if (key === 'isMTW') {
                        newEntry[key] = entry[key] ? 'Yes' : 'No';
                    } else if (key === 'subbed_price' || key === 'demurrage_cost' || key === 'costings') {
                        const value = parseFloat(entry[key]);
                        newEntry[key] = !isNaN(value) ? `£${value.toFixed(2)}` : '£0.00';
                    } else {
                        newEntry[key] = entry[key] ?? '—';
                    }
                });
                return newEntry;
            });
            setTableData(formatted);
            calculateSummaryStats(filtered);
            setIsFiltered(true);
        }
    };

    const toggleMtwFilter = () => {
        if (mtwFilter) {
            // Remove MTW filter - show all data
            showData();
            setMtwFilter(false);
            setIsFiltered(false);
        } else {
            // Apply MTW filter - show only MTW routes
            const filtered = subcontractedData.filter(entry => entry.isMTW === true);
            const formatted = filtered.map((entry) => {
                const newEntry = {};
                displayColumns.forEach(({ key }) => {
                    if (key === 'date') {
                        const dateObj = new Date(entry[key]);
                        newEntry[key] = isNaN(dateObj)
                            ? entry[key]
                            : `${String(dateObj.getDate()).padStart(2, '0')}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${dateObj.getFullYear()}`;
                    } else if (key === 'isMTW') {
                        newEntry[key] = entry[key] ? 'Yes' : 'No';
                    } else if (key === 'subbed_price' || key === 'demurrage_cost' || key === 'costings') {
                        const value = parseFloat(entry[key]);
                        newEntry[key] = !isNaN(value) ? `£${value.toFixed(2)}` : '£0.00';
                    } else {
                        newEntry[key] = entry[key] ?? '—';
                    }
                });
                return newEntry;
            });
            setTableData(formatted);
            calculateSummaryStats(filtered);
            setMtwFilter(true);
            setIsFiltered(true);
        }
    };

    const handleDownload = () => {
        if (!allowDownload) {
            showAlert({
                message: 'Download not allowed. Please verify your password first.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
            return;
        }

        if (!tableData || tableData.length === 0) {
            showAlert({
                message: 'No data available for download.',
                type: 'warning',
                buttons: [{ type: 'warning', label: 'Close' }],
            });
            return;
        }

        const csvData = tableData.map(row => {
            const csvRow = {};
            displayColumns.forEach(({ key, label }) => {
                csvRow[label] = row[key];
            });
            return csvRow;
        });

        downloadCSV(csvData);
        
        showAlert({
            message: 'CSV file downloaded successfully!',
            type: 'success',
            buttons: [{ type: 'success', label: 'OK' }],
        });
    };

    const uniqueCompanies = [...new Set(subcontractedData.map(item => item.subbed_company).filter(company => company && company !== '—'))];

    return (
        <div className={`mt-4 ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
            {/* Request Password Modal */}
            <RequestPassword 
                theme={theme}
                isOpen={openRequestPassword}
                setIsOpen={setOpenRequestPassword}
                returnData={handlePasswordSubmission}
            />

            {allowDownload && (
                <>
                    {/* Summary Stats Cards */}
                    <div className="row mb-4">
                        <div className="col-md-3 mb-3">
                            <div className={`card ${isLightTheme ? 'border-primary' : 'bg-secondary border-secondary'}`}>
                                <div className="card-body text-center">
                                    <h5 className={`card-title ${isLightTheme ? 'text-primary' : 'text-light'}`}>Total Routes</h5>
                                    <h3 className={isLightTheme ? 'text-dark' : 'text-light'}>{summaryStats.totalRoutes || 0}</h3>
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-md-3 mb-3">
                            <div className={`card ${isLightTheme ? 'border-success' : 'bg-secondary border-secondary'}`}>
                                <div className="card-body text-center">
                                    <h5 className={`card-title ${isLightTheme ? 'text-success' : 'text-light'}`}>Our Price Total</h5>
                                    <h3 className={isLightTheme ? 'text-dark' : 'text-light'}>{formatCurrency(summaryStats.totalOurPrice)}</h3>
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-md-3 mb-3">
                            <div className={`card ${isLightTheme ? 'border-warning' : 'bg-secondary border-secondary'}`}>
                                <div className="card-body text-center">
                                    <h5 className={`card-title ${isLightTheme ? 'text-warning' : 'text-light'}`}>Subbed Price Total</h5>
                                    <h3 className={isLightTheme ? 'text-dark' : 'text-light'}>{formatCurrency(summaryStats.totalSubbedPrice)}</h3>
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-md-3 mb-3">
                            <div className={`card ${summaryStats.profitLoss >= 0 ? (isLightTheme ? 'border-success' : 'bg-dark border-success') : (isLightTheme ? 'border-danger' : 'bg-dark border-danger')}`}>
                                <div className="card-body text-center">
                                    <h5 className={`card-title ${summaryStats.profitLoss >= 0 ? (isLightTheme ? 'text-success' : 'text-success') : (isLightTheme ? 'text-danger' : 'text-danger')}`}>
                                        {summaryStats.profitLoss >= 0 ? 'Profit' : 'Loss'}
                                    </h5>
                                    <h3 className={`${summaryStats.profitLoss >= 0 ? (isLightTheme ? 'text-success' : 'text-success') : (isLightTheme ? 'text-danger' : 'text-danger')}`} style={{ fontWeight: 'bold' }}>
                                        {formatCurrency(Math.abs(summaryStats.profitLoss))}
                                    </h3>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Additional Stats */}
                    <div className="row mb-4">
                        <div className="col-md-2 mb-3">
                            <div className={`card ${isLightTheme ? 'border-info' : 'bg-secondary border-secondary'}`}>
                                <div className="card-body text-center">
                                    <h6 className={`card-title ${isLightTheme ? 'text-info' : 'text-light'}`}>Subbed Companies</h6>
                                    <h4 className={isLightTheme ? 'text-dark' : 'text-light'}>{summaryStats.uniqueSubbedCompanies || 0}</h4>
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-md-2 mb-3">
                            <div 
                                className={`card ${mtwFilter ? (isLightTheme ? 'border-success bg-success' : 'bg-success border-success') : (isLightTheme ? 'border-primary' : 'bg-secondary border-secondary')}`}
                                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                onClick={toggleMtwFilter}
                                title={mtwFilter ? "Click to remove MTW filter" : "Click to filter MTW routes only"}
                            >
                                <div className="card-body text-center">
                                    <h6 className={`card-title ${mtwFilter ? 'text-white' : (isLightTheme ? 'text-primary' : 'text-light')}`}>
                                        MTW Routes {mtwFilter ? '✓' : ''}
                                    </h6>
                                    <h4 className={`${mtwFilter ? 'text-white' : (isLightTheme ? 'text-dark' : 'text-light')}`} style={{ fontWeight: 'bold' }}>
                                        {summaryStats.mtwRoutes || 0}
                                    </h4>
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-md-2 mb-3">
                            <div className={`card ${isLightTheme ? 'border-warning' : 'bg-secondary border-secondary'}`}>
                                <div className="card-body text-center">
                                    <h6 className={`card-title ${isLightTheme ? 'text-warning' : 'text-light'}`}>Demurrage Routes</h6>
                                    <h4 className={isLightTheme ? 'text-dark' : 'text-light'}>{summaryStats.demurrageRoutes || 0}</h4>
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-md-2 mb-3">
                            <div className={`card ${
                                summaryStats.profitPercentage <= 0 
                                    ? (isLightTheme ? 'border-danger' : 'bg-dark border-danger')
                                    : summaryStats.profitPercentage < 25
                                        ? (isLightTheme ? 'border-warning' : 'bg-dark border-warning')
                                        : (isLightTheme ? 'border-success' : 'bg-dark border-success')
                            }`}>
                                <div className="card-body text-center">
                                    <h6 className={`card-title ${
                                        summaryStats.profitPercentage <= 0 
                                            ? 'text-danger'
                                            : summaryStats.profitPercentage < 25
                                                ? 'text-warning'
                                                : 'text-success'
                                    }`}>Profit %</h6>
                                    <h4 className={`${
                                        summaryStats.profitPercentage <= 0 
                                            ? 'text-danger'
                                            : summaryStats.profitPercentage < 25
                                                ? 'text-warning'
                                                : 'text-success'
                                    }`} style={{ fontWeight: 'bold' }}>
                                        {summaryStats.profitPercentage ? `${summaryStats.profitPercentage.toFixed(1)}%` : '0.0%'}
                                    </h4>
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-md-4 mb-3">
                            <div className={`card ${isLightTheme ? 'border-secondary' : 'bg-secondary border-secondary'}`}>
                                <div className="card-body text-center">
                                    <h6 className={`card-title ${isLightTheme ? 'text-secondary' : 'text-light'}`}>Total Demurrage Cost</h6>
                                    <h4 className={isLightTheme ? 'text-dark' : 'text-light'}>{formatCurrency(summaryStats.totalDemurrageCost)}</h4>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Controls */}
                    <div className="row mb-3">
                        <div className="col-md-6">
                            <label className="form-label">Filter by Subbed Company:</label>
                            <select 
                                className={`form-select ${isLightTheme ? '' : 'bg-dark text-light border-secondary'}`}
                                onChange={(e) => filterByCompany(e.target.value)}
                            >
                                <option value="all">All Companies</option>
                                {uniqueCompanies.map((company, index) => (
                                    <option key={index} value={company}>{company}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-6 d-flex align-items-end">
                            <Button
                                color="success"
                                onClick={handleDownload}
                                className="me-2"
                            >
                                📊 Download CSV
                            </Button>
                            {isFiltered && (
                                <Button
                                    color="secondary"
                                    onClick={() => filterByCompany('all')}
                                >
                                    Clear Filter
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Data Table */}
                    <div className="table-responsive">
                        <table className={`table table-striped table-hover ${isLightTheme ? 'table-light' : 'table-dark'}`}>
                            <thead className={isLightTheme ? 'table-primary' : 'table-secondary'}>
                                <tr>
                                    {displayColumns.map(({ key, label }) => (
                                        <th key={key} scope="col">
                                            {label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {tableData.map((row, index) => (
                                    <tr key={index}>
                                        {displayColumns.map(({ key }) => (
                                            <td key={key}>{row[key]}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {tableData.length === 0 && (
                        <div className="text-center mt-4">
                            <p className={isLightTheme ? 'text-muted' : 'text-light'}>
                                No subcontracted routes found for the selected date range.
                            </p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default SubcontractedRoutesReport;